import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileText, Minus, Plus } from 'lucide-react';
import { Modal } from './Modal';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Button } from './Button';
import { SearchInput } from './SearchInput';
import { CurrencyDisplay } from './CurrencyDisplay';
import { useToast } from './Alert';
import { productsApi } from '../../api/endpoints/products';
import { useStore } from '../../contexts/StoreContext';
import { getCdnUrl } from '../../lib/formatters';
import type { Product } from '../../types/product';

interface QuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  /** Pre-loaded products list (e.g. from Products page). If omitted, the modal fetches products itself. */
  products?: Product[];
}

export function QuotationModal({ isOpen, onClose, storeId, products: externalProducts }: QuotationModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentStore } = useStore();

  const [customerName, setCustomerName] = useState('');
  const [validDays, setValidDays] = useState(15);
  const [notes, setNotes] = useState('');
  // Map of product id → quantity. A product is "selected" iff it has a key here;
  // dropping the key deselects. Quantity stays >= 1 while selected.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const selectedCount = Object.keys(quantities).length;

  // Fetch products if not provided externally
  const { data: fetchedProductsData } = useQuery({
    queryKey: ['products', storeId, { page: 1, limit: 100 }],
    queryFn: () => productsApi.list(storeId, { page: 1, limit: 100 }),
    enabled: isOpen && !externalProducts,
  });

  const allProducts = externalProducts ?? fetchedProductsData?.products ?? [];

  // Filter products by search term
  const filteredProducts = productSearch
    ? allProducts.filter((p) =>
        p.title.toLowerCase().includes(productSearch.toLowerCase())
      )
    : allProducts;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCustomerName('');
      setValidDays(15);
      setNotes('');
      setQuantities({});
      setProductSearch('');
    }
  }, [isOpen]);

  const toggleProduct = (id: string) => {
    setQuantities((prev) => {
      if (id in prev) {
        const { [id]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: 1 };
    });
  };

  const setQuantity = (id: string, qty: number) => {
    setQuantities((prev) => ({ ...prev, [id]: Math.max(1, Math.min(9999, Math.floor(qty) || 1)) }));
  };

  const handleGenerate = async () => {
    const items = Object.entries(quantities).map(([productId, quantity]) => ({ productId, quantity }));
    if (items.length === 0) return;
    setLoading(true);
    try {
      const blob = await productsApi.generateQuotation(storeId, {
        items,
        customerName: customerName || undefined,
        validDays,
        notes: notes || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotation-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      onClose();
    } catch {
      toast('error', t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('products.generateQuotation', 'Generate Quotation')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleGenerate}
            isLoading={loading}
            disabled={selectedCount === 0}
          >
            <FileText className="h-4 w-4" />
            {t('products.downloadQuotation', 'Download PDF')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={t('products.quotationCustomer', 'Customer Name')}
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
        />
        <Input
          label={t('products.quotationValidDays', 'Valid for (days)')}
          type="number"
          min="1"
          value={validDays}
          onChange={(e) => setValidDays(Number(e.target.value) || 15)}
        />
        <Textarea
          id="quotation-notes"
          label={t('products.quotationNotes', 'Notes')}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div>
          <label className="mb-2 block text-sm font-medium">
            {t('products.selectProducts', 'Select Products')} ({selectedCount})
          </label>
          <SearchInput
            value={productSearch}
            onChange={setProductSearch}
            placeholder={t('common.searchProducts', 'Search products...') }
            className="mb-2"
          />
          <div className="max-h-60 overflow-y-auto space-y-1 rounded-lg border border-border-default p-2">
            {filteredProducts.length === 0 ? (
              <p className="py-4 text-center text-sm text-text-muted">
                {t('common.noResults', 'No results')}
              </p>
            ) : (
              filteredProducts.map((p) => {
                const selected = p._id in quantities;
                const qty = quantities[p._id] ?? 1;
                return (
                  <div
                    key={p._id}
                    onClick={() => toggleProduct(p._id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleProduct(p._id);
                      }
                    }}
                    className={`flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-start text-sm transition-colors ${
                      selected
                        ? 'bg-accent/10 text-accent border border-accent/30'
                        : 'hover:bg-bg-hover border border-transparent'
                    }`}
                  >
                    {p.images?.[0] ? (
                      <img
                        src={getCdnUrl(p.images[0], currentStore?._id)}
                        alt=""
                        className="h-8 w-8 rounded object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-bg-tertiary" />
                    )}
                    <span className="flex-1 truncate">{p.title}</span>

                    {selected && (
                      <div
                        className="flex items-center gap-1 rounded-md border border-border-default bg-bg-secondary px-1 py-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          aria-label={t('common.decrease', 'Decrease')}
                          onClick={() => setQuantity(p._id, qty - 1)}
                          disabled={qty <= 1}
                          className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-text-primary hover:bg-bg-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={9999}
                          value={qty}
                          onChange={(e) => setQuantity(p._id, Number(e.target.value))}
                          onClick={(e) => e.stopPropagation()}
                          className="w-10 bg-transparent text-center text-[12px] font-semibold text-text-primary outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          aria-label={t('common.increase', 'Increase')}
                          onClick={() => setQuantity(p._id, qty + 1)}
                          className="flex h-6 w-6 items-center justify-center rounded text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    <CurrencyDisplay
                      amount={p.price}
                      currency={currentStore?.currency}
                      className="shrink-0"
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
